import { useEffect, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router-dom';
import { currentUser } from '../api/auth';
import { canMinMax } from '../lib/roles';
import AccessDenied from '../components/AccessDenied';
import Header from '../components/Header';
import { t } from '../lib/i18n';
import type { User } from '../api/types';

/**
 * Tab umbrella for /inventory. Salud (the existing health view) is the index
 * route; GMROI compares stores side-by-side; Velocidad is the ranked ABC SKU
 * list. Those three rely on the v2.6 inventory_skus.json cache, so no new
 * backend handlers — just SPA composition. Traspasos, Conteo físico and
 * Mín/máx came later and do have their own handlers.
 *
 * This strip is the ONLY entry point to these children: none of them appears
 * in the header nav, which lists /inventory itself and stops there.
 *
 * Accounting is hidden (same rule the existing inventory page applies);
 * staff sees Salud but the cost-strip applied by /api/inventory/skus makes
 * the GMROI and Velocidad tabs degenerate, so we route them through the
 * AccessDenied surface for clarity.
 */
export default function InventoryLayout() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const { pathname } = useLocation();
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  if (user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'accounting') {
    return (
      <div className="min-h-screen flex flex-col">
        <Header user={user} />
        <main className="flex-1 p-4"><AccessDenied user={user} /></main>
      </div>
    );
  }

  const tabClass = ({ isActive }: { isActive: boolean }) =>
    'inline-flex items-center px-3 py-2 text-sm border-b-2 -mb-px ' +
    (isActive
      ? 'border-tremor-brand text-tremor-content-strong font-semibold'
      : 'border-transparent text-tremor-content hover:text-tremor-content-strong');

  return (
    <div className="min-h-screen flex flex-col">
      {/* Shared store picker only on the tabs whose data honors it. */}
      <Header user={user} storeFilter={pathname === '/inventory' || pathname.startsWith('/inventory/velocidad')} />
      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-2xl mx-auto w-full">
        <div className="border-b border-tremor-border flex gap-1 overflow-x-auto">
          <NavLink to="/inventory" end className={tabClass}>{t.inventory.tabs.health}</NavLink>
          {user.role !== 'staff' && (
            <>
              <NavLink to="/inventory/gmroi"     className={tabClass}>{t.inventory.tabs.gmroi}</NavLink>
              <NavLink to="/inventory/velocidad" className={tabClass}>{t.inventory.tabs.velocity}</NavLink>
              <NavLink to="/inventory/traspasos" className={tabClass}>{t.inventory.tabs.transfers}</NavLink>
            </>
          )}
          {(user.permissions?.includes('physcount:use') || user.role === 'owner') && (
            <NavLink to="/inventory/conteo" className={tabClass}>{t.inventory.tabs.count}</NavLink>
          )}
          {canMinMax(user) && (
            <NavLink to="/inventory/minmax" className={tabClass}>{t.inventory.tabs.minmax}</NavLink>
          )}
        </div>
        <Outlet context={user} />
      </main>
    </div>
  );
}
